import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { clearSeriesSelection } from 'features/Series/calendar/seriesSelectionStore'
import { SERIES_PAGES_TEXT } from 'features/Series/pages/shared/seriesPagesText'
import { clearSeriesTab, requestSeriesTab } from 'features/Series/seriesTabIntent'

import { toDialogEvent } from './toDialogEvent'

const APPOINTMENT_PARAM = 'appointment'
const TAB_PARAM = 'tab'
const SERIES_TAB = 'series'
const NOTICE_TITLE = 'Series'
const POSITIVE_INTEGER = /^[1-9]\d*$/

type Args = {
  items: IPatientSchedule[] | undefined
  isReady: boolean
  hasFailed: boolean
  timeZone: string
  open: (event: IPatientSchedule) => void
}

const toAppointmentId = (raw: string): number | null => {
  if (!POSITIVE_INTEGER.test(raw)) return null
  const id = Number(raw)
  return Number.isSafeInteger(id) ? id : null
}

const withoutDeepLink = (params: URLSearchParams): URLSearchParams => {
  const next = new URLSearchParams(params)
  next.delete(APPOINTMENT_PARAM)
  next.delete(TAB_PARAM)
  return next
}

// Opens `?appointment={id}&tab=series` once the view holds that date's data, then clears the params.
export const useAppointmentDeepLink = ({
  items,
  isReady,
  hasFailed,
  timeZone,
  open,
}: Args): void => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { setNotification } = useNotification()
  const handledRef = useRef<string | null>(null)
  const raw = searchParams.get(APPOINTMENT_PARAM)
  const tab = searchParams.get(TAB_PARAM)

  useEffect(() => {
    if (raw === null) {
      handledRef.current = null
      return
    }
    if (handledRef.current === raw) return
    const id = toAppointmentId(raw)
    if (id !== null && !hasFailed && !isReady) return

    handledRef.current = raw
    setSearchParams(withoutDeepLink, { replace: true })
    if (id === null || hasFailed) return

    const event = toDialogEvent(items ?? [], id, timeZone)
    if (!event) {
      clearSeriesTab()
      setNotification({
        title: NOTICE_TITLE,
        type: ALERT_CONSTANTS.WARNING,
        description: SERIES_PAGES_TEXT.toasts.visitNotOnCalendar,
      })
      return
    }
    if (tab === SERIES_TAB) requestSeriesTab(event.id)
    clearSeriesSelection()
    open(event)
  }, [raw, tab, items, isReady, hasFailed, timeZone, open, setSearchParams, setNotification])
}
